import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Shield, Activity, Clock, RefreshCw, ChevronDown,
  User, DollarSign, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  admin_plan_change: { label: 'Plan Change', color: 'bg-blue-100 text-blue-700' },
  admin_wallet_grant: { label: 'Wallet Grant', color: 'bg-emerald-100 text-emerald-700' },
  admin_grant: { label: 'Credits Grant', color: 'bg-purple-100 text-purple-700' },
  admin_migration: { label: 'Migration', color: 'bg-zinc-100 text-zinc-700' },
  impersonation: { label: 'Impersonation', color: 'bg-amber-100 text-amber-700' },
};

function getActionMeta(type) {
  return ACTION_LABELS[type] || { label: type || 'Unknown', color: 'bg-zinc-100 text-zinc-600' };
}

function relativeTime(isoString) {
  if (!isoString) return 'Unknown';
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function formatTimestamp(isoString) {
  if (!isoString) return 'Unknown';
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
}

export default function AuditLog({ adminToken }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState('');
  const [adminActor, setAdminActor] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const axiosConfig = { headers: { Authorization: `Bearer ${adminToken}` } };

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actionType) params.action_type = actionType;
      if (adminActor.trim()) params.admin_actor = adminActor.trim();

      const response = await axios.get(`${API}/api/admin/audit-log`, {
        ...axiosConfig,
        params
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [actionType, adminActor, adminToken]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // Stats — count across all loaded entries (up to limit=200)
  const stats = {
    total: entries.length,
    impersonations: entries.filter(e => e.action_type === 'impersonation').length,
    planChanges: entries.filter(e => e.action_type === 'admin_plan_change').length,
    walletGrants: entries.filter(e => e.action_type === 'admin_wallet_grant').length,
  };

  const toggleRow = (idx) => setExpandedRow(expandedRow === idx ? null : idx);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-600" />
          <h2 className="text-xl font-bold text-zinc-900">Audit Log</h2>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAuditLog} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-4 w-4 text-indigo-500" />
            <span className="text-xs text-zinc-500">Total Loaded</span>
          </div>
          <p className="text-2xl font-bold text-zinc-900">{stats.total}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <User className="h-4 w-4 text-amber-500" />
            <span className="text-xs text-zinc-500">Impersonations</span>
          </div>
          <p className="text-2xl font-bold text-zinc-900">{stats.impersonations}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-4 w-4 text-blue-500" />
            <span className="text-xs text-zinc-500">Plan Changes</span>
          </div>
          <p className="text-2xl font-bold text-zinc-900">{stats.planChanges}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            <span className="text-xs text-zinc-500">Wallet Grants</span>
          </div>
          <p className="text-2xl font-bold text-zinc-900">{stats.walletGrants}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={actionType}
          onChange={(e) => setActionType(e.target.value)}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Action Types</option>
          <option value="admin_plan_change">Plan Change</option>
          <option value="admin_wallet_grant">Wallet Grant</option>
          <option value="admin_grant">Credits Grant</option>
          <option value="admin_migration">Migration</option>
          <option value="impersonation">Impersonation</option>
        </select>
        <Input
          placeholder="Filter by admin..."
          value={adminActor}
          onChange={(e) => setAdminActor(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchAuditLog()}
          className="w-56"
        />
        <span className="text-sm text-zinc-400">{entries.length} entries</span>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="text-left p-3 text-zinc-600 font-medium">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    Timestamp
                  </div>
                </th>
                <th className="text-left p-3 text-zinc-600 font-medium">Admin</th>
                <th className="text-left p-3 text-zinc-600 font-medium">Action</th>
                <th className="text-left p-3 text-zinc-600 font-medium">Target User</th>
                <th className="text-left p-3 text-zinc-600 font-medium">Details</th>
                <th className="text-left p-3 text-zinc-600 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <RefreshCw className="h-6 w-6 animate-spin text-indigo-400 mx-auto" />
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-zinc-400">
                    No audit log entries found
                  </td>
                </tr>
              ) : (
                entries.map((entry, idx) => {
                  const meta = getActionMeta(entry.action_type);
                  const isExpanded = expandedRow === idx;
                  return (
                    <React.Fragment key={idx}>
                      <tr
                        className="border-b hover:bg-zinc-50 cursor-pointer"
                        onClick={() => toggleRow(idx)}
                      >
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-zinc-800">{relativeTime(entry.timestamp)}</p>
                            <p className="text-xs text-zinc-400">{formatTimestamp(entry.timestamp)}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-zinc-700">{entry.admin_actor || '—'}</span>
                        </td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${meta.color}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="p-3">
                          <div>
                            {entry.target_user_email && (
                              <p className="font-medium text-zinc-700">{entry.target_user_email}</p>
                            )}
                            {entry.target_user_id && (
                              <p className="text-xs text-zinc-400 font-mono">
                                {entry.target_user_id.substring(0, 8)}...
                              </p>
                            )}
                            {!entry.target_user_email && !entry.target_user_id && (
                              <span className="text-zinc-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-zinc-500 max-w-xs truncate">
                          {entry.details_dict
                            ? Object.entries(entry.details_dict)
                                .filter(([, v]) => v !== null && v !== undefined)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(', ') || '—'
                            : '—'}
                        </td>
                        <td className="p-3">
                          <ChevronDown
                            className={`h-4 w-4 text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-zinc-50 border-b">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="text-xs font-mono text-zinc-600 bg-white border border-zinc-200 rounded p-3 overflow-auto max-h-48">
                              <pre>{JSON.stringify(entry, null, 2)}</pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
